/**
 * Responsibility: show a card's picture in a list row, and cope when there
 * isn't one.
 *
 * Worth having rather than decorative. A row of numbers about "Chansey-Holo
 * #70" is hard to hold in your head across fifty rows; the picture is how a
 * person actually recognises which card is being talked about, and it is the
 * fastest way to catch the screen having matched the wrong printing.
 *
 * Six percent of the dataset has no image, so the empty state is a real case
 * rather than an edge one. It renders as a deliberate placeholder - a broken
 * image element reads as a bug, whereas an obvious gap reads as missing data.
 * The same applies when a CDN is slow or blocks the request: onError falls
 * back to the placeholder instead of leaving a torn icon in the table.
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

type CardThumbProps = {
  src: string | null;
  alt: string;
  /** Rendered width in pixels. Cards are 5:7, so height follows. */
  width?: number;
};

const CARD_RATIO = 7 / 5;

export function CardThumb({ src, alt, width = 48 }: CardThumbProps) {
  const [failed, setFailed] = useState(false);
  const height = Math.round(width * CARD_RATIO);

  if (!src || failed) {
    return (
      <Box
        sx={{
          width,
          height,
          flexShrink: 0,
          borderRadius: 1,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'action.hover',
          color: 'text.disabled',
        }}
        aria-label={`${alt} (no image)`}
      >
        <ImageNotSupportedIcon sx={{ fontSize: width * 0.4 }} />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{
        width,
        height,
        flexShrink: 0,
        borderRadius: 1,
        objectFit: 'cover',
        // Cards are light on the front and the table is dark, so a hairline
        // keeps the edge from bleeding into the row.
        border: '1px solid',
        borderColor: 'divider',
      }}
    />
  );
}
